import { createFileRoute, redirect } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { getSafeCliRedirectUrl } from "@/common/cli-redirect";
import {
  getGatewayLoginUrl,
  hasGatewaySession,
} from "@/common/gateway-session";
import { captureOutboundSignupAttribution } from "@/common/outbound-signup-attribution";

const signupSearchSchema = z.object({
  redirect_url: z.string().optional(),
  r: z.string({ coerce: true }).optional(),
  product: z.string().optional(),
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_content: z.string().optional(),
  utm_term: z.string().optional(),
  // An org-invitation accept token. Kept in the schema so invite links keep
  // parsing; the accept flow itself rides redirect_url (the invite-accept
  // page re-runs after login).
  invite: z.string().optional(),
  // The email typed into the landing's email + Join field. Forwarded to Tank
  // as login_hint to prefill its form; /auth/start validates it.
  email: z.string().optional(),
});

export async function checkSignupSessionBeforeCapture(
  search: z.infer<typeof signupSearchSchema>,
  getStorage: () => Storage | undefined,
): Promise<boolean> {
  const hasSession = await hasGatewaySession();
  if (!hasSession) {
    try {
      const storage = getStorage();
      if (storage) {
        captureOutboundSignupAttribution(search, storage);
      }
    } catch {
      // Accessing window.localStorage itself can throw in sandboxed or
      // storage-restricted browsers. Attribution is best effort; auth is not.
    }
  }
  return hasSession;
}

// /signup stays as a stable entry point (invite emails, marketing CTAs) but
// holds no auth UI of its own: an already-signed-in visitor bounces to the
// (sanitized) redirect target, everyone else hands off to /auth/start, which
// redirects into Tank's hosted sign-up.
export const Route = createFileRoute("/_rootLayout/signup")({
  validateSearch: zodValidator(signupSearchSchema),
  ssr: false,
  beforeLoad: async ({ search, preload }) => {
    // Preload (defaultPreload: "intent") must not run the redirect — it uses
    // reloadDocument, which would hard-navigate on hover. The real click
    // re-runs beforeLoad without preload. Mirrors admin.route.
    if (preload) return;
    // Sanitize the caller-supplied redirect_url through the allowlist so only
    // safe same-origin/product URLs are ever bounced to or forwarded
    // (defense-in-depth open-redirect guard; the backend validates too).
    const safeRedirectUrl = getSafeCliRedirectUrl(search.redirect_url);
    const hasSession = await checkSignupSessionBeforeCapture(search, () =>
      typeof window === "undefined" ? undefined : window.localStorage,
    );
    if (hasSession) {
      throw redirect(
        safeRedirectUrl.startsWith("/")
          ? { to: safeRedirectUrl }
          : { href: safeRedirectUrl },
      );
    }
    throw redirect({
      href: getGatewayLoginUrl(
        search.redirect_url ? safeRedirectUrl : undefined,
        "signup",
        search.email,
        Boolean(search.invite),
      ),
      // Relative href (/auth/start) would client-route into the server-only
      // Nitro route → SPA not-found; reloadDocument forces the real doc load.
      reloadDocument: true,
    });
  },
  component: SignUpRoute,
});

// beforeLoad always redirects on a real navigation; this renders only for the
// preload early-return, so keep it inert.
function SignUpRoute() {
  return null;
}
